import { Component, DestroyRef, inject, signal } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GenericDropdownComponent } from 'libs/shared/src/lib/components/generic-dropdown/generic-dropdown.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { catchError, finalize, of, tap } from 'rxjs';
import { ProductAttributeService } from '../../services/product-attribute.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DynamicRouteHandlingService } from 'libs/shared/src/lib/services/dynamic-route-handling.service';
import { Store } from '@ngrx/store';
import { AppState } from 'apps/cinari-erp-client/src/app/store/app.reducers';
import { selectCategoryListByProductListId, selectProductList, selectSubcategories } from '@zayan-erp/stores';
import { AsyncPipe } from '@angular/common';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import * as HomeBuildingActions from '@zayan-erp/stores';
import { ProductAttribute } from '../../models/product.model';
import { AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import { SpinnerService } from '@zayan-erp/shared';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'lib-attribute-type-form',
  imports: [
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    GenericDropdownComponent,
    AsyncPipe,
    ToggleSwitchModule,
    AutoCompleteModule
  ],
  templateUrl: './attribute-type-form.component.html'
})
export class AttributeTypeFormComponent {
  #productAttributeService = inject(ProductAttributeService);
  #router = inject(Router);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #activatedRoute = inject(ActivatedRoute);
  #store = inject(Store<AppState>);
  #destroyRef = inject(DestroyRef);
  #spinner = inject(SpinnerService);
  #toastr = inject(ToastrService);

  selectedAttributeTypeId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id') ?? null);
  selectedCategoryId = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('categoryId') ?? null);
  selectedProductListId = signal<string | null>(
    this.#activatedRoute.snapshot.queryParamMap.get('productListId') ?? null
  );
  productList$ = this.#store.select(selectProductList);
  subcategory$ = this.#store.select(selectSubcategories(this.selectedCategoryId() ?? ''));
  category$ = this.#store.select(selectCategoryListByProductListId(this.selectedProductListId() ?? ''));
  attributeTypeSuggestions = signal<string[]>([]);
  selectedFromSuggestions = signal<boolean>(false);
  apiResponseItems = signal<string[]>([]); // Track items from API response
  subcategoryIdWhenFetched = signal<string | null>(null); // Track subcategory when API was called

  constructor() {
    this.#dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.submitAttributeTypeForm();
    });

    this.attributeTypeForm
      .get('productListId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(productListId => {
        this.attributeTypeForm.get('categoryId')?.setValue(null);
        if (productListId) {
          this.#store.dispatch(HomeBuildingActions.loadCategoryListByProductListId({ productListId: productListId }));
        }
        this.category$ = this.#store.select(selectCategoryListByProductListId(productListId!));
      });

    this.attributeTypeForm
      .get('categoryId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(categoryId => {
        this.attributeTypeForm.get('subcategoryId')?.setValue(null);
        this.selectedFromSuggestions.set(false); // Reset when category changes
        this.apiResponseItems.set([]); // Reset API response items when category changes
        this.subcategoryIdWhenFetched.set(null); // Reset tracked subcategory when category changes
        if (categoryId) {
          this.#store.dispatch(
            HomeBuildingActions.loadSubcategories({
              productListId: this.selectedProductListId()!,
              categoryListId: categoryId
            })
          );
        }
        this.subcategory$ = this.#store.select(selectSubcategories(categoryId!));
      });
  }

  attributeTypeForm = new FormGroup({
    id: new FormControl<string | null>(null),
    productListId: new FormControl<string | null>(
      {
        value: this.#activatedRoute.snapshot.queryParamMap.get('productListId') ?? null,
        disabled: !!this.selectedAttributeTypeId()
      },
      Validators.required
    ),
    categoryId: new FormControl<string | null>(
      {
        value: this.#activatedRoute.snapshot.queryParamMap.get('categoryId') ?? null,
        disabled: !!this.selectedAttributeTypeId()
      },
      Validators.required
    ),
    subcategoryId: new FormControl<string | null>(
      {
        value: this.#activatedRoute.snapshot.queryParamMap.get('subcategoryId') ?? null,
        disabled: !!this.selectedAttributeTypeId()
      },
      Validators.required
    ),
    name: new FormControl<string | null>(null, Validators.required),
    validate: new FormControl<boolean>(true, Validators.required),
    isActive: new FormControl<boolean>(true, Validators.required)
  });

  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedAttributeTypeId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#productAttributeService.readProductAttributeById(params.id).pipe(
          tap((attribute: ProductAttribute) => {
            this.editForm(attribute);
          })
        );
      }
      return of(null);
    }
  });

  editForm(attribute: ProductAttribute) {
    this.attributeTypeForm.patchValue({
      id: attribute.id,
      name: attribute.name,
      validate: attribute.validate
    });
  }

  submitAttributeTypeForm() {
    if (this.attributeTypeForm.invalid) {
      this.attributeTypeForm.markAllAsTouched();
      return;
    }
    if (this.selectedAttributeTypeId()) {
      this.#productAttributeService.updateProductAttribute(this.attributeTypeForm.value).subscribe(() => {
        this.attributeTypeForm.reset();
        this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
        this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
      });
    } else {
      const name = (this.attributeTypeForm.get('name')?.value ?? '').toString().trim();
      const categoryId = this.attributeTypeForm.get('categoryId')?.value ?? null;
      const currentSubcategoryId = this.attributeTypeForm.get('subcategoryId')?.value ?? null;
      if (!name || !categoryId || !currentSubcategoryId) {
        return;
      }
      const nameLower = name.toLowerCase();
      const apiItems = this.apiResponseItems();
      const fetchedSubcategoryId = this.subcategoryIdWhenFetched();
      if (apiItems.length && fetchedSubcategoryId && fetchedSubcategoryId === currentSubcategoryId) {
        const matchedApiName = apiItems.find(item => item.toLowerCase() === nameLower);
        if (matchedApiName) {
          if (!this.selectedFromSuggestions()) {
            this.#toastr.error('Please select the attribute from the autocomplete list.');
            return;
          }
          if (matchedApiName !== name) {
            this.#toastr.error(`Please select "${matchedApiName}" from suggestions to keep the original casing.`);
            return;
          }
        }
      }
      const createAttributeType = () => {
        this.#productAttributeService.createProductAttribute(this.attributeTypeForm.value).subscribe(() => {
          this.attributeTypeForm.reset();
          this.selectedFromSuggestions.set(false);
          this.apiResponseItems.set([]);
          this.subcategoryIdWhenFetched.set(null);
          this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], {
            queryParamsHandling: 'preserve'
          });
          this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
        });
      };

      this.#productAttributeService
        .readProductAttribute(currentSubcategoryId)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: list => {
            const exists =
              Array.isArray(list) &&
              list.some(attribute => (attribute?.name?.trim() ?? '').toLowerCase() === nameLower);
            if (exists) {
              this.#toastr.error('Attribute Type already exists');
              return;
            }
            createAttributeType();
          },
          error: () => {
            createAttributeType();
          }
        });
    }
  }

  onAttributeTypeSearch(event: { query: string }) {
    const term = (event?.query ?? '').trim();
    const termLower = term.toLowerCase();
    const categoryId = this.attributeTypeForm.get('categoryId')?.value ?? null;
    const subcategoryId = this.attributeTypeForm.get('subcategoryId')?.value ?? null;
    this.selectedFromSuggestions.set(false);
    if (!term) {
      this.attributeTypeSuggestions.set([]);
      this.apiResponseItems.set([]);
      this.subcategoryIdWhenFetched.set(null);
      this.#spinner.handleSpinnerStop();
      return;
    }
    if (!categoryId) {
      this.attributeTypeSuggestions.set([term]);
      this.apiResponseItems.set([]);
      this.subcategoryIdWhenFetched.set(null);
      this.#spinner.handleSpinnerStop();
      return;
    }
    this.attributeTypeSuggestions.set([term]);
    this.subcategoryIdWhenFetched.set(subcategoryId);
    this.#productAttributeService
      .searchAttributeTypes(categoryId, term)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => this.#spinner.handleSpinnerStop())
      )
      .subscribe({
        next: apiNames => {
          const items = Array.isArray(apiNames) ? apiNames : [];
          this.apiResponseItems.set(items);
          if (subcategoryId) {
            this.#productAttributeService
              .readProductAttribute(subcategoryId)
              .pipe(
                takeUntilDestroyed(this.#destroyRef),
                catchError(() => of<ProductAttribute[]>([]))
              )
              .subscribe({
                next: existing => {
                  const attributeNames = (existing ?? [])
                    .map(attribute => attribute?.name?.trim() ?? '')
                    .filter(name => !!name);
                  const filteredExisting = attributeNames.filter(name => name.toLowerCase().includes(termLower));
                  const filteredApi = items.filter(name => name.toLowerCase().includes(termLower));
                  const suggestions = Array.from(new Set<string>([term, ...filteredExisting, ...filteredApi]));
                  this.attributeTypeSuggestions.set(suggestions);
                }
              });
          } else {
            const filteredApi = items.filter(name => name.toLowerCase().includes(termLower));
            const suggestions = Array.from(new Set<string>([term, ...filteredApi]));
            this.attributeTypeSuggestions.set(suggestions);
          }
        },
        error: () => {
          if (subcategoryId) {
            this.#productAttributeService
              .readProductAttribute(subcategoryId)
              .pipe(
                takeUntilDestroyed(this.#destroyRef),
                catchError(() => of<ProductAttribute[]>([]))
              )
              .subscribe({
                next: existing => {
                  const attributeNames = (existing ?? [])
                    .map(attribute => attribute?.name?.trim() ?? '')
                    .filter(name => !!name);
                  const filteredExisting = attributeNames.filter(name => name.toLowerCase().includes(termLower));
                  const suggestions = Array.from(new Set<string>([term, ...filteredExisting]));
                  this.attributeTypeSuggestions.set(suggestions);
                }
              });
          } else {
            this.apiResponseItems.set([]);
            this.attributeTypeSuggestions.set([term]);
          }
        }
      });
  }

  onAttributeTypeSelect() {
    this.selectedFromSuggestions.set(true);
  }
}
